import {
  CMD,
  FrameStreamDecoder,
  bytesToHex,
} from "../protocol/frameProtocol";

const textDecoder = new TextDecoder();

let currentPort = null;
let currentReader = null;
let reading = false;
const dataListeners = new Set();
const statusListeners = new Set();
const frameWaiters = new Set();
const frameDecoder = new FrameStreamDecoder();

function cleanHex(hex = "") {
  return String(hex).replace(/0x/gi, "").replace(/[^a-fA-F0-9]/g, "").toUpperCase();
}

const toHex = bytesToHex;

export function isWebSerialSupported() {
  return typeof navigator !== "undefined" && "serial" in navigator;
}

export function isWebSerialSecureContext() {
  return typeof window !== "undefined" && window.isSecureContext;
}

export function isWebSerialConnected() {
  return Boolean(currentPort?.writable);
}

export function getWebSerialStatus() {
  return {
    supported: isWebSerialSupported(),
    secureContext: isWebSerialSecureContext(),
    connected: isWebSerialConnected(),
  };
}

function emitStatus(extra = {}) {
  const status = {
    ...getWebSerialStatus(),
    ...extra,
  };

  statusListeners.forEach((listener) => listener(status));
  return status;
}

function emitData(payload) {
  dataListeners.forEach((listener) => listener(payload));
}

function notifyProtocolFrame(frame) {
  emitData({
    direction: "frame",
    source: "web-serial",
    receivedAt: new Date().toISOString(),
    ...frame,
  });

  frameWaiters.forEach((waiter) => {
    if (!waiter.predicate(frame)) return;
    frameWaiters.delete(waiter);
    clearTimeout(waiter.timer);
    waiter.resolve(frame);
  });
}

function createFrameWaiter(predicate, timeoutMs = 1600) {
  let waiter;
  const promise = new Promise((resolve, reject) => {
    waiter = {
      predicate,
      resolve,
      reject,
      timer: setTimeout(() => {
        frameWaiters.delete(waiter);
        reject(new Error(`Tiempo de espera agotado (${timeoutMs} ms)`));
      }, timeoutMs),
    };
    frameWaiters.add(waiter);
  });

  return {
    promise,
    cancel() {
      if (!waiter) return;
      frameWaiters.delete(waiter);
      clearTimeout(waiter.timer);
    },
  };
}

export function onWebSerialData(listener) {
  dataListeners.add(listener);
  return () => dataListeners.delete(listener);
}

export function onWebSerialStatus(listener) {
  statusListeners.add(listener);
  listener(getWebSerialStatus());
  return () => statusListeners.delete(listener);
}

export function hexToUint8Array(hex) {
  const clean = cleanHex(hex);

  if (!clean) {
    return new Uint8Array();
  }

  if (clean.length % 2 !== 0) {
    throw new Error(`HEX inválido, longitud impar: ${clean}`);
  }

  const bytes = new Uint8Array(clean.length / 2);

  for (let i = 0; i < clean.length; i += 2) {
    bytes[i / 2] = parseInt(clean.slice(i, i + 2), 16);
  }

  return bytes;
}

async function startReadLoop() {
  if (reading || !currentPort?.readable) return;

  reading = true;

  try {
    while (currentPort?.readable) {
      currentReader = currentPort.readable.getReader();

      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await currentReader.read();

          if (done) break;

          if (value) {
            const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
            emitData({
              direction: "rx",
              data: textDecoder.decode(bytes, { stream: true }),
              hex: toHex(bytes),
              receivedAt: new Date().toISOString(),
            });

            const decoded = frameDecoder.push(bytes);
            decoded.frames.forEach(notifyProtocolFrame);
            decoded.errors.forEach((frameError) => {
              emitData({
                direction: "frame-error",
                source: "web-serial",
                receivedAt: new Date().toISOString(),
                ...frameError,
              });
            });
          }
        }
      } catch (error) {
        if (currentPort) {
          emitData({
            direction: "error",
            error: error.message,
            receivedAt: new Date().toISOString(),
          });
        }
      } finally {
        try {
          currentReader.releaseLock();
        } catch {
          // ignore release errors
        }
        currentReader = null;
      }
    }
  } finally {
    reading = false;
  }
}

export async function connectWebSerial({ baudRate = 115200 } = {}) {
  if (!isWebSerialSupported()) {
    throw new Error(
      "Web Serial no está disponible. Usa Chrome/Edge y abre la app en HTTPS o localhost."
    );
  }

  if (currentPort?.readable || currentPort?.writable) {
    return emitStatus({ message: "Puerto local ya conectado" });
  }

  currentPort = await navigator.serial.requestPort();

  await currentPort.open({
    baudRate: Number(baudRate || 115200),
    dataBits: 8,
    stopBits: 1,
    parity: "none",
    flowControl: "none",
  });

  emitStatus({ message: `Puerto local abierto @ ${baudRate}` });
  frameDecoder.reset();
  startReadLoop();

  return getWebSerialStatus();
}

export async function disconnectWebSerial() {
  try {
    if (currentReader) {
      await currentReader.cancel().catch(() => {});
    }

    if (currentPort) {
      await currentPort.close();
    }
  } finally {
    currentReader = null;
    currentPort = null;
    reading = false;
    frameDecoder.reset();
    emitStatus({ message: "Puerto local cerrado" });
  }
}

export async function writeWebSerialBytes(bytes, label = "frame") {
  if (!currentPort?.writable) {
    throw new Error("No hay puerto local Web Serial conectado. Abre Monitor Serial y pulsa Conectar tarjeta local.");
  }

  const uint8Bytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const writer = currentPort.writable.getWriter();

  try {
    await writer.write(uint8Bytes);
    emitData({
      direction: "tx",
      label,
      hex: toHex(uint8Bytes),
      sentAt: new Date().toISOString(),
    });
  } finally {
    writer.releaseLock();
  }
}

export async function writeWebSerialHex(hex, label = "frame") {
  const bytes = hexToUint8Array(hex);
  await writeWebSerialBytes(bytes, label);
}

export async function writeWebSerialText(text) {
  const encoder = new TextEncoder();
  await writeWebSerialBytes(encoder.encode(text), "text");
}

export async function writeFrameList(
  frames = [],
  { waitForAck = true, ackTimeoutMs = 1600, retries = 1 } = {}
) {
  if (!Array.isArray(frames) || frames.length === 0) {
    throw new Error("No hay tramas para enviar");
  }

  for (const frame of frames) {
    if (!frame?.frameHex) continue;
    let sent = false;
    let lastError;

    for (let attempt = 0; attempt <= retries && !sent; attempt += 1) {
      const waiter = waitForAck
        ? createFrameWaiter(
            (incoming) =>
              incoming.subsys === frame.subsys &&
              incoming.seq === frame.seq &&
              [CMD.ACK, CMD.NACK].includes(incoming.cmd) &&
              (incoming.details?.requestCommand === undefined ||
                incoming.details?.requestCommand === frame.cmd),
            ackTimeoutMs
          )
        : null;

      try {
        await writeWebSerialHex(frame.frameHex, frame.label || `SEQ ${frame.seq}`);
        if (waiter) {
          const response = await waiter.promise;
          if (response.cmd === CMD.NACK) {
            throw new Error(
              `NACK para SEQ ${frame.seq}, error 0x${Number(response.details?.errorCode ?? 0)
                .toString(16)
                .padStart(2, "0")}`
            );
          }
        }
        sent = true;
      } catch (error) {
        waiter?.cancel();
        lastError = error;
      }
    }

    if (!sent) {
      throw new Error(
        `No se confirmó SEQ ${frame.seq} (${frame.label || "frame"}): ${lastError?.message || "sin ACK"}`
      );
    }
  }

  return frames.length;
}

export async function buildProtocolCommand(
  apiBase,
  body,
  { reserveSequence = false } = {}
) {
  const response = await fetch(
    `${apiBase}/protocol/command${reserveSequence ? "?reserveSeq=true" : ""}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, reserveSeq: reserveSequence }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.ok) {
    throw new Error(data?.error || `Error generando comando (${response.status})`);
  }
  return data;
}

export async function sendProtocolCommandViaWebSerial(
  apiBase,
  body,
  { expectedCommand, timeoutMs = 1800 } = {}
) {
  const connected = isWebSerialConnected();
  const data = await buildProtocolCommand(apiBase, body, {
    reserveSequence: connected,
  });

  if (!connected) {
    return { ...data, generatedOnly: true, sent: false, response: null };
  }

  const expected = expectedCommand === undefined
    ? null
    : typeof expectedCommand === "string"
      ? CMD[String(expectedCommand).toUpperCase()]
      : expectedCommand;
  const waiter = expected === null
    ? null
    : createFrameWaiter(
        (incoming) => {
          if (incoming.subsys !== data.subsys) return false;
          if (incoming.seq !== data.seq) return false;
          if (incoming.cmd === CMD.NACK) {
            return incoming.details?.requestCommand === undefined ||
              incoming.details?.requestCommand === data.cmd;
          }
          if (incoming.cmd !== expected) return false;
          if (expected === CMD.IMAGE_RESULT && body.algorithm !== undefined) {
            const responseSeq = incoming.details?.imageChunk?.requestSeq;
            return responseSeq === undefined || responseSeq === data.seq;
          }
          if (expected === CMD.ACK) {
            return incoming.details?.requestCommand === undefined ||
              incoming.details?.requestCommand === data.cmd;
          }
          return true;
        },
        timeoutMs
      );

  try {
    await writeWebSerialHex(data.frameHex, data.command || "command");
    const response = waiter ? await waiter.promise : null;
    if (response?.cmd === CMD.NACK) {
      throw new Error(
        `NACK para SEQ ${data.seq}, error 0x${Number(response.details?.errorCode ?? 0)
          .toString(16)
          .padStart(2, "0")}`
      );
    }
    return { ...data, generatedOnly: false, sent: true, response };
  } catch (error) {
    waiter?.cancel();
    throw error;
  }
}

export async function buildFramesFromBackend(
  apiBase,
  body,
  { reserveSequence = false } = {}
) {
  const endpoint = reserveSequence
    ? `${apiBase}/uart/send-config?dryRun=true&reserveSeq=true`
    : `${apiBase}/protocol/preview`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...body,
      dryRun: true,
      reserveSeq: reserveSequence,
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.ok) {
    throw new Error(data?.error || `Error generando tramas (${response.status})`);
  }

  return data;
}

export async function sendSemanticConfigViaWebSerial(apiBase, body) {
  if (!isWebSerialConnected()) {
    const data = await buildFramesFromBackend(apiBase, body);
    return {
      ...data,
      webSerial: {
        sent: false,
        framesSent: 0,
        generatedOnly: true,
      },
    };
  }

  const data = await buildFramesFromBackend(apiBase, body, {
    reserveSequence: true,
  });
  const framesSent = await writeFrameList(data.frames, {
    waitForAck: true,
    ackTimeoutMs: 1600,
    retries: 1,
  });

  return {
    ...data,
    webSerial: {
      sent: true,
      framesSent,
      acked: true,
    },
  };
}
